import React, { useContext, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Checkbox, Dropdown, Grid, Header, Icon, Popup, Ref } from 'semantic-ui-react'
import { ClippedContent, CollapsibleList, DateTimeUtils, EllipsisDropdownPortal, Notification, OnHoverOnClick, Person, PriorityIcon, STATUS_TYPES, Table, TemperatureIcon } from 'dyl-components';

import {
    KEY_CREATED, KEY_OWNER_ID, KEY_LAST_MODIFIED,
    KEY_ACCOUNT, KEY_STAGE, KEY_CAMPAIGN_SOURCE,
    KEY_CONVERT, KEY_ACTIONS, KEY_CREATED_BY, KEY_OUTCOME, KEY_LAST_MODIFIED_BY,
    KEY_CO_OWNER,
    KEY_DATA_PROVIDER,
    KEY_CALLBACK,
    KEY_LOST_REASON,
    KEY_MASTER_SECONDARY,
    KEY_PRODUCT_INTEREST,
    KEY_LEAD_PRIORITY,
    KEY_LEAD_RATING,
    KEY_LEAD_SCORE
} from 'shared/constants/MODULE_COLUMNS';

import Contact from 'shared/Contact';
import AccountLink from 'shared/AccountLink';
import ModuleLink from 'shared/ModuleLink';
import ContactConversionControls from 'shared/ContactConversionControls';
import { useDispatch } from 'react-redux';
import leadsActions from 'actions/leads';
import HoverContent from 'shared/hoverContent';
import { BulkActionsContext } from 'shared/context/BulkActionsProvider';
import RestrictedView from 'shared/RestrictedView';
import { USER_PERMISSIONS } from 'dyl-components';
import "./index.scss"

const LeadsRow = ({
    lead,
    isColumnHidden,
    filterQueryParameters,
    setSelectedLead,
    setModalRef,
    priority_options = [],
    rating_options = [],
    onSelectLeadForConversion
}) => {
    const { contact, account, pipeline_stage_summary, outcome = '', created_by, owned_by, co_owned_by, last_modified_by, close_reason, close_type, pipeline_master_source, pipeline_secondary_source, product_interests, custom_data } = lead;
    const { fields } = custom_data || {};
    const [selectedLeads, setSelectedLeads] =
        useContext(BulkActionsContext);
    const isSelected = (lead_id) => {
        return selectedLeads.includes(lead_id);
    };

    const onToggleLead = (lead_id) => {
        const newSelectedLeads = isSelected(lead_id)
            ? selectedLeads.filter((id) => lead_id !== id)
            : [...selectedLeads, lead_id];
        setSelectedLeads(newSelectedLeads);
    };
    const dispatch = useDispatch();

    const refresh = () => {
        dispatch(leadsActions.readLeads(filterQueryParameters()));
    }
    const ref = useRef();
    const leadPriority = priority_options.find(option => option.value === Number(fields?.priority))?.text;
    const leadRating = rating_options.find(option => option.value === Number(fields?.rating))?.text;
    const handleSkipCallback = async () => {
        try {
            await dispatch(leadsActions.skipCallbacks(lead?.contact?.id, { id: lead?.callbacks[0]?.id, call_type: lead?.callbacks[0]?.call_type }));
            refresh();
        } catch (error) {
            Notification.alert('Failed to skip callback', STATUS_TYPES.ERROR, true);
        }
    }
    return (
        <Table.Row>
            <Table.Cell style={{ position: "relative" }}>
                {lead?.uncontacted && <Popup
                    trigger={
                        <div className='LeadsRow--notConnected' />
                    }
                    content="Uncontacted"
                    position='top left'
                    inverted
                    offset={[-17.5, 0]}
                />}
                <Checkbox
                    checked={isSelected(lead?.contact?.id)}
                    onChange={() => {
                        onToggleLead(lead?.contact?.id);
                    }}
                />
            </Table.Cell>
            {
                !isColumnHidden(KEY_CREATED) &&
                <Table.Cell>
                    <div><b>{DateTimeUtils.formatEpoch(lead.pipeline_created, DateTimeUtils.WORD_DATE_FORMAT)}</b></div>
                    <div>{DateTimeUtils.formatEpoch(lead.pipeline_created, DateTimeUtils.TIME_FORMAT)}</div>
                </Table.Cell>
            }
            {
                !isColumnHidden(KEY_CREATED_BY) &&
                <Table.Cell>
                    {created_by && (
                        <Person
                            username={created_by?.username}
                            email={created_by?.email}
                            isInactiveUser={!created_by?.active}
                        />
                    )}
                </Table.Cell>
            }
            {
                !isColumnHidden(KEY_LAST_MODIFIED) &&
                <Table.Cell>
                    {
                        lead.pipeline_created !== lead.pipeline_activity && (
                            <React.Fragment>
                                <div><b>{lead.pipeline_activity && DateTimeUtils.formatEpoch(lead.pipeline_activity, DateTimeUtils.WORD_DATE_FORMAT)}</b></div>
                                <div>{lead.pipeline_activity && DateTimeUtils.formatEpoch(lead.pipeline_activity, DateTimeUtils.TIME_FORMAT)}</div>
                            </React.Fragment>
                        )
                    }

                </Table.Cell>
            }
            {
                !isColumnHidden(KEY_LAST_MODIFIED_BY) &&
                <Table.Cell>
                    {last_modified_by && (
                        <Person
                            username={last_modified_by?.username}
                            email={last_modified_by?.email}
                            isInactiveUser={!last_modified_by?.active}
                        />
                    )}
                </Table.Cell>
            }
            <Table.Cell>
                <ModuleLink pipeline_id={lead.pipeline_id} isLeadConnect={lead.lead_connect} record={lead}/>
            </Table.Cell>

            <Table.Cell>
                <Contact
                    contact={{
                        ...contact,
                        email: contact?.primary_email || {},
                        phone: contact?.primary_phone || {}
                    }}
                    name={`${contact.first_name} ${contact.last_name}`}
                    linkToContact={
                    <RestrictedView type="link" userPermission={USER_PERMISSIONS.CONTACTS_VIEW} userRestrict assignmentIds={[(lead?.contact?.owner_id || 0), (lead?.contact?.coowner_id || 0)]}>
                        <Link to={`/contact/${contact.id}`}>{`${contact.first_name} ${contact.last_name}`}</Link>
                    </RestrictedView>
                    }
                />
            </Table.Cell>
            {!isColumnHidden(KEY_DATA_PROVIDER) && (
                <Table.Cell>
                    <Header as='h5'>
                        {lead?.source_data?.data_provider_name}
                        <Header.Subheader>
                            {lead?.source_data?.data_record_name}
                        </Header.Subheader>
                    </Header>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_ACCOUNT) && (
                <Table.Cell >
                    {account?.id &&
                        <AccountLink
                            id={account.id}
                            name={account.name}
                            type={account.type}
                            account={lead.account}
                        />
                    }
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_STAGE) && (
                <Table.Cell>
                    <Header as='h5'>
                        {pipeline_stage_summary?.pipeline_stage_name}
                        <Header.Subheader>
                            {pipeline_stage_summary?.pipeline_category_name}
                        </Header.Subheader>
                    </Header>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CALLBACK) && (
                <Table.Cell>
                    {lead?.callbacks?.length > 0 && <Popup
                        trigger={
                            <div style={{ cursor: 'pointer' }}>
                                <div><b>{DateTimeUtils.formatEpoch(lead.callbacks[0]?.time, DateTimeUtils.WORD_DATE_FORMAT)}</b></div>
                                <a href>{DateTimeUtils.formatEpoch(lead.callbacks[0]?.time, DateTimeUtils.TIME_FORMAT)}</a>
                            </div>
                        }
                        content={
                            <div>
                                <Header as="h3">Next Callback</Header>

                                {lead?.callbacks?.map((callback, index) => (
                                    <Grid
                                        key={index}
                                        columns={3}
                                        verticalAlign="middle"
                                    >
                                        <Grid.Column width={7}>
                                            {DateTimeUtils.formatEpoch(callback?.time, DateTimeUtils.WORD_DATE_FORMAT)}
                                        </Grid.Column>

                                        <Grid.Column width={2} style={{ fontWeight: "bold" }}>
                                            {DateTimeUtils.formatEpoch(callback?.time, DateTimeUtils.WORD_DAY_FORMAT).toUpperCase()}
                                        </Grid.Column>

                                        <Grid.Column width={7} textAlign="right">
                                            {DateTimeUtils.formatEpoch(callback?.time, DateTimeUtils.TIME_FORMAT)}
                                        </Grid.Column>
                                    </Grid>
                                ))}
                            </div>
                        }
                        position='bottom center'
                        
                    />}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_OUTCOME) && (
                <Table.Cell>
                    {
                        outcome !== '' ?
                            <h5 className={`ModulesListTable__Outcome${outcome.split('(')[0] === 'won' ? '--won' : '--lost'}`}>
                                {outcome.charAt(0).toUpperCase() + outcome.slice(1).replace('(', ' (')}
                            </h5> : ''
                    }
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_LOST_REASON) && (
                <Table.Cell>
                    <div className='OpportunityTable__MediumFixedWidth'>
                        <ClippedContent>
                            {close_reason?.name && close_type?.name ? `${close_reason?.name} / ${close_type?.name}` : ''}
                        </ClippedContent>
                    </div>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_MASTER_SECONDARY) && (
                <Table.Cell>
                    <div className='OpportunityTable__MediumFixedWidth'>
                        <ClippedContent>
                            {`${pipeline_master_source ? pipeline_master_source : ''}${pipeline_secondary_source ? `/${pipeline_secondary_source.name}` : ''}`}
                        </ClippedContent>
                    </div>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_PRODUCT_INTEREST) && (
                <Table.Cell>
                    <div className='OpportunityTable__MediumFixedWidth'>

                        {product_interests?.length > 0 && (
                            <CollapsibleList
                                minimumNumberOfItemsToShow={3}
                                items={product_interests?.map(product_interest => (<ClippedContent>
                                    {product_interest.name}
                                </ClippedContent>))}
                            />
                        )}
                    </div>
                </Table.Cell>
            )}
            {
                !isColumnHidden(KEY_LEAD_PRIORITY) && (
                    <Table.Cell disabled={outcome}>
                        {fields?.priority &&
                            <React.Fragment>
                                <PriorityIcon type={leadPriority?.toLowerCase()} /> {leadPriority}
                            </React.Fragment>
                        }
                    </Table.Cell>
                )
            }
            {
                !isColumnHidden(KEY_LEAD_RATING) && (
                    <Table.Cell disabled={outcome}>
                        {fields?.rating &&
                            <React.Fragment>
                                <TemperatureIcon type={leadRating?.toLowerCase()} /> {leadRating}
                            </React.Fragment>
                        }
                    </Table.Cell>
                )
            }
            {
                !isColumnHidden(KEY_LEAD_SCORE) && (
                    <Table.Cell disabled={outcome}>
                        {fields?.score}
                    </Table.Cell>
                )
            }
            {!isColumnHidden(KEY_OWNER_ID) && (
                <Table.Cell>
                    {owned_by ? <OnHoverOnClick
                        hoverContent={
                            <HoverContent
                                user={owned_by}
                            />
                        }
                        clickable={false}
                        trigger={owned_by ?
                            <div className='TableTruncateFixedWidth'>
                                <Person
                                    username={`${owned_by.first_name} ${owned_by.last_name}`}
                                    email={owned_by.email}
                                    isInactiveUser={!owned_by.active}
                                    popup={false}
                                    title={" "}
                                />
                            </div>
                            : undefined}
                    /> : "Unassigned"}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CO_OWNER) && (
                <Table.Cell>
                    {co_owned_by ? <OnHoverOnClick
                        hoverContent={
                            <HoverContent
                                user={co_owned_by}
                            />
                        }
                        clickable={false}
                        trigger={co_owned_by ?
                            <div className='TableTruncateFixedWidth'>
                                <Person
                                    username={`${co_owned_by.first_name} ${co_owned_by.last_name}`}
                                    email={co_owned_by.email}
                                    isInactiveUser={!co_owned_by.active}
                                    popup={false}
                                    title={" "}
                                />
                            </div>
                            : undefined}
                    /> : "Unassigned"}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CAMPAIGN_SOURCE) && (
                <Table.Cell>

                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CONVERT) && (
                <Table.Cell>
                    {outcome === '' && !(contact.id === account?.pipeline_person_id) ?
                        <ContactConversionControls
                            account_id={account?.id}
                            account_stage={'lead'}
                            contactName={`${contact.first_name} ${contact.last_name}`}
                            contact_id={contact.id}
                            linkedAccountIsACustomer={account?.customer}
                            linkedAccountName={account?.name}
                            linkedAccountPrimaryContact={account?.primary_person_id}
                            refresh={refresh}
                            isDeactivatedCustomer={account?.customer_status === 'canceled' || account?.customer_status === 'paused'}
                            location={contact.location}
                            phone={contact.primary_phone}
                            email={contact.primary_email}
                            contact={lead}
                            onSelectForConversion={onSelectLeadForConversion}
                        />
                        : ''}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_ACTIONS) && (
                <Table.Cell>
                    {outcome === '' && (
                        <Ref innerRef={ref}>
                            <EllipsisDropdownPortal reference={ref}>
                                <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.LEAD_EDIT} userRestrict assignmentIds={[(lead?.contact?.owner_id || 0), (lead?.contact?.coowner_id || 0)]}>
                                    {lead?.lead_connect && lead?.callbacks?.length > 0 && (
                                    <Dropdown.Item disabled={! lead?.callbacks[0].time} text={<React.Fragment>
                                        <Icon name='fas fa-xmark' color='red' />
                                        Skip Callback
                                    </React.Fragment>}
                                        style={{ cursor: 'pointer' }}
                                        onClick={handleSkipCallback}
                                    />
                                )}
                                <Dropdown.Item disabled={lead?.callbacks?.length} onClick={() => { setSelectedLead(lead); setModalRef(ref); }} text={<React.Fragment>
                                        <Icon name={'fas fa-sitemap'} />
                                        Sequences
                                    </React.Fragment>}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </RestrictedView> 
                            </EllipsisDropdownPortal>
                        </Ref>
                    )}
                </Table.Cell>
            )}

        </Table.Row>
    );
}

export default LeadsRow;
